import React, { useState, useEffect, useMemo } from "react";
import Drawer from "../Drawer/Drawer";
import PdfViewer from "../PdfViewer/PdfViewer";
import { Document } from "../../types/chat.types";
import { apiService } from "../../services/chatApi";
import { getGATitle } from "../../utils/gaMapping";
import "./DocumentExplorer.css";

interface DocumentExplorerProps {
  isOpen: boolean;
  onClose: () => void;
}

// Extract GA number from filename (e.g., "GA013.pdf" -> "GA013")
const extractGANumber = (filename: string): string => {
  const match = filename.match(/^(GA\d+)/i);
  return match ? match[1].toUpperCase() : filename.replace(/\.pdf$/i, "");
};

const DocumentExplorer: React.FC<DocumentExplorerProps> = ({ isOpen, onClose }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  // Enrich documents with GA numbers and titles
  const documentsWithTitles = useMemo(() => {
    return documents.map(doc => {
      const gaNumber = extractGANumber(doc.name);
      return {
        ...doc,
        gaNumber,
        title: doc.title || getGATitle(gaNumber)
      };
    });
  }, [documents]);

  // Filter documents based on search term
  const filteredDocuments = useMemo(() => {
    if (searchTerm.trim() === "") {
      return documentsWithTitles;
    }
    const searchLower = searchTerm.toLowerCase();
    return documentsWithTitles.filter((doc) => {
      return (
        doc.name.toLowerCase().includes(searchLower) ||
        doc.gaNumber.toLowerCase().includes(searchLower) ||
        (doc.title && doc.title.toLowerCase().includes(searchLower))
      );
    });
  }, [searchTerm, documentsWithTitles]);

  useEffect(() => {
    if (isOpen && documents.length === 0) {
      loadDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllDocuments();
      // Sort by name (GA number)
      const sorted = [...response.documents].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );
      setDocuments(sorted);
    } catch (err) {
      setError("Fehler beim Laden der Dokumente");
      console.error("Error loading documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (doc: Document & { gaNumber: string; title?: string }) => {
    // Convert Document to SearchResult format for PdfViewer
    const searchResult = {
      document_name: doc.name,
      excerpt: "",
      context: "",
      index: 0,
      marker: "",
      similarity: 1.0,
      title: doc.title
    };
    setSelectedDocument(searchResult);
    setPdfViewerOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("de-DE");
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        position="left"
        width="400px"
        title="Dokumenten-Explorer"
        showCloseButton={true}
      >
        <div className="document-explorer">
          {/* Search Bar */}
          <div className="document-search">
            <input
              type="text"
              placeholder="Dokumente durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="document-search-input"
            />
            {searchTerm && (
              <button
                className="document-search-clear"
                onClick={() => setSearchTerm("")}
                title="Suche löschen"
              >
                ✕
              </button>
            )}
          </div>

          {/* Statistics */}
          <div className="document-statistics">
            <div className="stat-item">
              <span className="stat-icon">📄</span>
              <span className="stat-value">{filteredDocuments.length}</span>
              <span className="stat-label">
                {filteredDocuments.length === documents.length
                  ? "Dokumente"
                  : `von ${documents.length}`}
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="document-loading">
              <div className="loading-spinner"></div>
              <p>Lade Dokumente...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="document-error">
              <p>{error}</p>
              <button onClick={loadDocuments} className="retry-button">
                Erneut versuchen
              </button>
            </div>
          )}

          {/* Document List */}
          {!loading && !error && (
            <div className="document-list">
              {filteredDocuments.length === 0 && searchTerm && (
                <div className="no-results">
                  <p>Keine Dokumente gefunden für "{searchTerm}"</p>
                </div>
              )}

              {filteredDocuments.map((doc) => (
                <div
                  key={doc.name}
                  className="document-item"
                  onClick={() => handleDocumentClick(doc)}
                  title={doc.title ? `${doc.gaNumber} – ${doc.title}` : doc.gaNumber}
                >
                  <div className="document-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 2V8H20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div className="document-info">
                    <div className="document-name">
                      {doc.gaNumber}
                      {doc.title && <span className="document-title"> – {doc.title}</span>}
                    </div>
                    <div className="document-meta">
                      <span>{formatFileSize(doc.size)}</span>
                      <span className="meta-separator">•</span>
                      <span>{formatDate(doc.modified)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>

      {/* PDF Viewer Modal */}
      {selectedDocument && (
        <PdfViewer
          open={pdfViewerOpen}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedDocument(null);
          }}
          reference={selectedDocument}
          isDirectLink={false}
        />
      )}
    </>
  );
};

export default DocumentExplorer;
